import { useState, useEffect, type ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import {
  Box,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import type { IAtributo } from "interfaces/loja/atributosProduto";
import type {
  IVariacaoProdutoState,
  IVariacaoAtributoPayload,
} from "interfaces/loja/variacaoProduto";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { currencyMaskBRL, currencyToNumber, formatCurrency } from "helpers/utils";
import {
  DRAWER_WIDTH,
  drawerFooterSx,
  drawerPaperSx,
  drawerSectionHeaderSx,
  drawerSectionSx,
  drawerSelectControlSx,
  formFieldsWrapperSx,
  modalHeaderSx,
  saveButtonSx,
  switchAccentSx,
  toggleCardSx,
} from "../produto-form.theme";

export interface VariacaoDrawerProps {
  open: boolean;
  onClose: () => void;
  variacao: IVariacaoProdutoState | null;
  index: number;
  atributos: IAtributo[];
  onSalvar: (index: number, variacao: IVariacaoProdutoState) => void;
}

const variacaoVazia = (): IVariacaoProdutoState => ({
  sku: "",
  codigoBarras: "",
  preco: 0,
  precoOriginal: 0,
  precoCusto: null,
  estoque: 0,
  emEstoque: true,
  isPadrao: false,
  atributos: [],
});

function DrawerSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={drawerSectionSx}>
      <Box sx={drawerSectionHeaderSx}>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ p: 2, ...formFieldsWrapperSx }}>{children}</Box>
    </Box>
  );
}

export default function VariacaoDrawer({
  open,
  onClose,
  variacao,
  index,
  atributos,
  onSalvar,
}: VariacaoDrawerProps) {
  const [edit, setEdit] = useState<IVariacaoProdutoState>(variacaoVazia());

  useEffect(() => {
    if (variacao) {
      setEdit({
        ...variacaoVazia(),
        ...variacao,
        atributos: [...(variacao.atributos || [])],
      });
    } else {
      setEdit(variacaoVazia());
    }
  }, [variacao, open]);

  const handleSalvar = () => {
    const precoPadraoNum = currencyToNumber(edit.preco ?? 0);
    const precoOriginalNum = currencyToNumber(edit.precoOriginal ?? 0);
    const custoNum = currencyToNumber(edit.precoCusto ?? 0);
    onSalvar(index, {
      ...edit,
      estoque: Number(edit.estoque) || 0,
      preco: precoPadraoNum,
      precoOriginal: precoOriginalNum,
      precoCusto: custoNum > 0 ? custoNum : null,
    });
    onClose();
  };

  const updateAtributo = (
    atributoId: number,
    payload: IVariacaoAtributoPayload
  ) => {
    setEdit((prev) => {
      const rest = (prev.atributos || []).filter(
        (a) => a.atributoId !== atributoId
      );
      return {
        ...prev,
        atributos: [...rest, payload],
      };
    });
  };

  const atributosListaOuCor = atributos.filter(
    (a) => a.tipo === "LISTA" || a.tipo === "COR"
  );

  const precoVenda = currencyToNumber(edit.preco ?? 0);
  const skuPreview = edit.sku?.trim() || "Sem SKU";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": drawerPaperSx,
      }}
    >
      <Box sx={modalHeaderSx}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
            <LayersOutlinedIcon sx={{ fontSize: 32, opacity: 0.9, flexShrink: 0 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.1 }}>
                Variação do produto
              </Typography>
              <Typography variant="h6" fontWeight="bold" noWrap>
                Editar variação
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, display: "block", mt: 0.25 }}
                noWrap
              >
                {skuPreview}
                {precoVenda > 0 ? ` · ${formatCurrency(precoVenda)}` : ""}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          p: 2.5,
          bgcolor: "grey.50",
        }}
      >
        <Stack spacing={2}>
          <DrawerSection title="Identificação" subtitle="Códigos usados no estoque e na loja">
            <Stack spacing={2}>
              <FormField
                label="SKU"
                value={edit.sku ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEdit((p) => ({ ...p, sku: e.target.value }))
                }
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <FormField
                label="Código de barras"
                value={edit.codigoBarras ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEdit((p) => ({ ...p, codigoBarras: e.target.value }))
                }
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DrawerSection>

          <DrawerSection title="Preços" subtitle="Valores exibidos na vitrine">
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5}>
                <FormField
                  label="Preço de venda"
                  type="text"
                  value={currencyMaskBRL(
                    String(Math.round(currencyToNumber(edit.preco ?? 0) * 100))
                  )}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEdit((p) => ({
                      ...p,
                      preco: (e.target.value || "").replace(/\D/g, "") as any,
                    }))
                  }
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <FormField
                  label="Preço original"
                  type="text"
                  value={currencyMaskBRL(
                    String(
                      Math.round(currencyToNumber(edit.precoOriginal ?? 0) * 100)
                    )
                  )}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEdit((p) => ({
                      ...p,
                      precoOriginal: (e.target.value || "").replace(
                        /\D/g,
                        ""
                      ) as any,
                    }))
                  }
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              <FormField
                label="Preço de custo (CMV)"
                type="text"
                value={currencyMaskBRL(
                  String(Math.round(currencyToNumber(edit.precoCusto ?? 0) * 100))
                )}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEdit((p) => ({
                    ...p,
                    precoCusto: (e.target.value || "").replace(/\D/g, "") as any,
                  }))
                }
                variant="outlined"
                fullWidth
                helperText="Uso interno — não aparece na loja."
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DrawerSection>

          <DrawerSection title="Estoque" subtitle="Disponibilidade e variação principal">
            <Stack spacing={2}>
              <FormField
                label="Quantidade em estoque"
                type="number"
                value={edit.estoque ?? 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEdit((p) => ({ ...p, estoque: Number(e.target.value) || 0 }))
                }
                variant="outlined"
                fullWidth
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
              />
              <Stack spacing={1.5}>
                <Box sx={toggleCardSx(Boolean(edit.emEstoque))}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(edit.emEstoque)}
                        onChange={(_, checked) =>
                          setEdit((p) => ({ ...p, emEstoque: checked }))
                        }
                        sx={switchAccentSx}
                      />
                    }
                    label={
                      <Box pr={1}>
                        <Typography variant="body2" fontWeight={600}>
                          Em estoque
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {edit.emEstoque ? "Disponível para compra" : "Indisponível"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ m: 0, alignItems: "center" }}
                  />
                </Box>
                <Box sx={toggleCardSx(Boolean(edit.isPadrao))}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(edit.isPadrao)}
                        onChange={(_, checked) =>
                          setEdit((p) => ({ ...p, isPadrao: checked }))
                        }
                        sx={switchAccentSx}
                      />
                    }
                    label={
                      <Box pr={1}>
                        <Typography variant="body2" fontWeight={600}>
                          Variação padrão
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {edit.isPadrao ? "Selecionada ao abrir o produto" : "Variação secundária"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ m: 0, alignItems: "center" }}
                  />
                </Box>
              </Stack>
            </Stack>
          </DrawerSection>

          {atributosListaOuCor.length > 0 ? (
            <DrawerSection
              title="Atributos"
              subtitle="Tamanho, cor e outras opções desta variação"
            >
              <Stack spacing={2}>
                {atributosListaOuCor.map((atr) => {
                  const opcoes = atr.opcoes || [];
                  const current = (edit.atributos || []).find(
                    (a) => a.atributoId === atr.id
                  );

                  if (atr.tipo === "LISTA") {
                    const value = current?.opcaoId ?? "";
                    return (
                      <FormControl
                        key={atr.id}
                        fullWidth
                        variant="outlined"
                        sx={drawerSelectControlSx}
                      >
                        <InputLabel id={`atr-${atr.id}`}>{atr.nome}</InputLabel>
                        <Select
                          labelId={`atr-${atr.id}`}
                          label={atr.nome}
                          value={value}
                          onChange={(e) => {
                            const opcaoId = e.target.value as number;
                            updateAtributo(atr.id, { atributoId: atr.id, opcaoId });
                          }}
                        >
                          <MenuItem value="">— Não definido</MenuItem>
                          {opcoes.map((op) => (
                            <MenuItem key={op.id} value={op.id}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }

                  const valueCor = current?.valorCor ?? "";
                  return (
                    <FormControl
                      key={atr.id}
                      fullWidth
                      variant="outlined"
                      sx={drawerSelectControlSx}
                    >
                      <InputLabel id={`atr-${atr.id}`}>{atr.nome}</InputLabel>
                      <Select
                        labelId={`atr-${atr.id}`}
                        label={atr.nome}
                        value={valueCor}
                        onChange={(e) => {
                          const valorCor = e.target.value as string;
                          updateAtributo(atr.id, { atributoId: atr.id, valorCor });
                        }}
                        renderValue={(selected) => {
                          const op = opcoes.find((o) => o.valor === selected);
                          if (!selected) return "— Não definido";
                          return (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  bgcolor: String(selected),
                                  border: "1px solid",
                                  borderColor: "grey.300",
                                  flexShrink: 0,
                                }}
                              />
                              <span>{op?.label ?? selected}</span>
                            </Stack>
                          );
                        }}
                      >
                        <MenuItem value="">— Não definido</MenuItem>
                        {opcoes.map((op) => (
                          <MenuItem key={op.id} value={op.valor}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  bgcolor: op.valor,
                                  border: "1px solid",
                                  borderColor: "grey.300",
                                  flexShrink: 0,
                                }}
                              />
                              <span>{op.label}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                })}
              </Stack>
            </DrawerSection>
          ) : null}
        </Stack>
      </Box>

      <Box sx={drawerFooterSx}>
        <Stack spacing={1.25}>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={handleSalvar}
            fullWidth
            sx={saveButtonSx}
          >
            Salvar variação
          </MDButton>
          <MDButton
            variant="text"
            color="dark"
            onClick={onClose}
            fullWidth
            sx={{ color: "text.secondary", fontWeight: 600 }}
          >
            Cancelar
          </MDButton>
        </Stack>
      </Box>
    </Drawer>
  );
}
